/**
 * Iter 253h · Email branding per workspace.
 *
 * Header/footer HTML, unsubscribe + manage-prefs URLs, brand color, reply-to
 * and physical postal address. Auto-wraps every outbound email sent via
 * `email_service.send_email(..., workspace_id=ws)` — that includes waitlist,
 * password reset, project alerts, cashflow digests, HR nudges — everything.
 *
 * CAN-SPAM compliance: default footer includes an unsubscribe link + the
 * workspace's physical address whenever admins fill them in.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Save, Palette, RotateCcw, Eye, Mail, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "@/components/PageHeader";
import { apiErrorMessage } from "@/lib/apiError";


export default function CommsBranding() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [b, setB] = useState({});
  const [defaultFooter, setDefaultFooter] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    if (!wsId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/comms/branding`);
      setB(data);
      setDefaultFooter(data._default_footer || "");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [wsId]);   // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/workspaces/${wsId}/comms/branding`, {
        header_html: b.header_html || "",
        footer_html: b.footer_html || "",
        unsubscribe_url: b.unsubscribe_url || "",
        manage_prefs_url: b.manage_prefs_url || "",
        brand_color: b.brand_color || "#0f172a",
        reply_to: b.reply_to || "",
        physical_address: b.physical_address || "",
      });
      setB({ ...data, _default_footer: defaultFooter });
      toast.success("Branding saved · applies to next outbound email");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setSaving(false); }
  };

  const resetFooter = () => setB({ ...b, footer_html: defaultFooter });

  // Render preview (client-side, quick substitution for standard vars)
  const previewHtml = () => {
    const ctx = {
      workspace_name: "Your Workspace",
      unsubscribe_url: b.unsubscribe_url || "#",
      manage_prefs_url: b.manage_prefs_url || b.unsubscribe_url || "#",
      physical_address: b.physical_address || "",
      brand_color: b.brand_color || "#0f172a",
    };
    const substitute = (s) => (s || "").replace(/{{\s*(\w+)\s*}}/g, (_, k) => ctx[k] ?? `{{${k}}}`);
    const header = substitute(b.header_html || "");
    const body = `<div style="padding:24px 0"><h2 style="color:${ctx.brand_color};margin:0 0 12px">Sample email subject</h2><p style="color:#374151;line-height:1.6">Hi there,</p><p style="color:#374151;line-height:1.6">This is a preview of an outbound email wrapped with your workspace's branding. Everything above the divider is your header; everything below is your footer.</p></div>`;
    const footer = substitute(b.footer_html || defaultFooter);
    return header + body + footer;
  };

  if (busy) return <div className="p-6 text-sm text-muted-foreground">Loading branding…</div>;

  return (
    <div className="p-6 md:p-8 space-y-5 max-w-5xl mx-auto" data-testid="comms-branding-page">
      <PageHeader
        eyebrow="Communications"
        title="Email branding"
        description="Header, footer, unsubscribe links and brand colour — applied automatically to every outbound email from this workspace."
      />

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <div className="label-eyebrow mb-1 flex items-center gap-1"><Mail className="w-3 h-3" /> Reply-to email</div>
            <Input value={b.reply_to || ""} onChange={(e) => setB({ ...b, reply_to: e.target.value })}
                      placeholder="hello@yourdomain.com" data-testid="branding-reply-to" />
            <p className="text-[10px] text-muted-foreground mt-0.5">Recipients reply here — leave blank to use system default.</p>
          </div>
          <div>
            <div className="label-eyebrow mb-1 flex items-center gap-1"><Palette className="w-3 h-3" /> Brand color</div>
            <div className="flex items-center gap-2">
              <input type="color" value={b.brand_color || "#0f172a"}
                        onChange={(e) => setB({ ...b, brand_color: e.target.value })}
                        className="h-8 w-16 border border-border cursor-pointer"
                        data-testid="branding-color" />
              <Input value={b.brand_color || "#0f172a"} onChange={(e) => setB({ ...b, brand_color: e.target.value })}
                        className="font-mono" />
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">Available inside templates as <code>{"{{brand_color}}"}</code>.</p>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Unsubscribe URL</div>
            <Input value={b.unsubscribe_url || ""} onChange={(e) => setB({ ...b, unsubscribe_url: e.target.value })}
                      placeholder="https://yourdomain.com/unsubscribe?token={{recipient_token}}"
                      data-testid="branding-unsub-url" />
            <p className="text-[10px] text-muted-foreground mt-0.5">Required for CAN-SPAM / GDPR compliance in marketing sends.</p>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Manage preferences URL</div>
            <Input value={b.manage_prefs_url || ""} onChange={(e) => setB({ ...b, manage_prefs_url: e.target.value })}
                      placeholder="https://yourdomain.com/prefs?token={{recipient_token}}"
                      data-testid="branding-prefs-url" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Physical postal address</div>
            <Textarea rows={2} value={b.physical_address || ""}
                          onChange={(e) => setB({ ...b, physical_address: e.target.value })}
                          placeholder="123 Main St, Suite 400, City, State ZIP, Country"
                          data-testid="branding-address" />
            <p className="text-[10px] text-muted-foreground mt-0.5">Legally required in every commercial email (CAN-SPAM §5).</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <div className="label-eyebrow mb-1">Header HTML</div>
            <Textarea rows={5} value={b.header_html || ""}
                          onChange={(e) => setB({ ...b, header_html: e.target.value })}
                          placeholder={`<div style="text-align:center;padding:16px"><img src="https://yourdomain.com/logo.png" alt="Logo" style="height:32px"></div>`}
                          className="font-mono text-xs"
                          data-testid="branding-header-html" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="label-eyebrow">Footer HTML</div>
              <button type="button" onClick={resetFooter}
                       className="text-[10px] px-2 py-0.5 border border-border hover:border-primary hover:text-primary flex items-center gap-1"
                       data-testid="branding-reset-footer">
                <RotateCcw className="w-3 h-3" /> Reset to default
              </button>
            </div>
            <Textarea rows={7} value={b.footer_html || ""}
                          onChange={(e) => setB({ ...b, footer_html: e.target.value })}
                          className="font-mono text-xs"
                          data-testid="branding-footer-html" />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              Vars: <code>{"{{workspace_name}}"}</code>, <code>{"{{unsubscribe_url}}"}</code>, <code>{"{{manage_prefs_url}}"}</code>, <code>{"{{physical_address}}"}</code>, <code>{"{{brand_color}}"}</code>
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
        <button type="button" onClick={() => setPreviewOpen(true)}
                 className="px-3 py-2 border border-border hover:bg-muted text-sm flex items-center gap-1.5"
                 data-testid="branding-preview-btn">
          <Eye className="w-4 h-4" /> Preview
        </button>
        <button type="button" onClick={save} disabled={saving}
                 className="px-4 py-2 border border-primary bg-primary text-primary-foreground text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
                 data-testid="branding-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save branding"}
        </button>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-background/80 flex items-center justify-center p-6" data-testid="branding-preview-dialog">
          <div className="bg-background border border-border shadow-lg w-[720px] max-h-[80vh] overflow-y-auto">
            <div className="border-b border-border p-3 flex items-center justify-between">
              <div className="text-sm font-bold">Preview</div>
              <button type="button" onClick={() => setPreviewOpen(false)}
                       className="text-muted-foreground hover:text-foreground text-xs"
                       data-testid="branding-preview-close">Close</button>
            </div>
            <div className="p-6 bg-white">
              <div className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: previewHtml() }}
                    data-testid="branding-preview-content" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
